import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Loader2, RefreshCw, CheckCircle2, Sparkles, ChevronDown, ChevronRight, AlertTriangle, Copy, Check } from "lucide-react";

export default function IntentGaps() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [suggesting, setSuggesting] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = (withResolved = includeResolved) => {
    setLoading(true);
    api.get(`/gaps?include_resolved=${withResolved}`)
      .then((res) => setData(res.data))
      .catch(() => toast.error("Couldn't load intent gaps."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeResolved]);

  const handleSuggest = async (cluster) => {
    setSuggesting(cluster.signature);
    try {
      const res = await api.post("/gaps/suggest", {
        questions: cluster.examples.map((e) => e.question),
        label: cluster.label,
      });
      setSuggestions((prev) => ({ ...prev, [cluster.signature]: res.data.suggestion }));
    } catch {
      toast.error("Couldn't draft a fix - try again.");
    } finally {
      setSuggesting(null);
    }
  };

  const handleResolve = async (cluster, resolved) => {
    try {
      await api.post("/gaps/resolve", { gap_ids: cluster.gap_ids, resolved });
      toast.success(resolved ? `Marked "${cluster.label}" resolved.` : "Reopened.");
      load();
    } catch {
      toast.error("Couldn't update - try again.");
    }
  };

  const copySuggestion = (sig, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(sig);
      setTimeout(() => setCopied(null), 1600);
    });
  };

  return (
    <div className="md:col-span-10 h-full glass-panel rounded-2xl p-6 overflow-y-auto" data-testid="intent-gaps-panel">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-white text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-rose-400" /> Intent Gap Analysis
          </h2>
          <p className="text-[11px] text-slate-500 mt-1">
            Questions AVA couldn't answer well across the whole platform - where public documentation is lacking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="gaps-toggle-resolved"
            onClick={() => setIncludeResolved((v) => !v)}
            className={`text-[10px] font-mono-data uppercase tracking-wider rounded-full px-3 py-1.5 border transition-colors ${includeResolved ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" : "text-slate-400 border-white/10 hover:border-white/20"}`}
          >
            {includeResolved ? "Showing resolved" : "Show resolved"}
          </button>
          <button
            data-testid="gaps-refresh"
            onClick={() => load()}
            className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-rose-300 bg-rose-400/10 hover:bg-rose-400/20 border border-rose-400/30 rounded-full px-3 py-1.5 transition-colors"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-3 my-5">
          <div className="bg-black/30 border border-white/10 rounded-xl p-4" data-testid="gaps-stat-open">
            <p className="text-2xl font-display font-bold text-rose-300">{data.open_gaps}</p>
            <p className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 mt-1">Open gaps</p>
          </div>
          <div className="bg-black/30 border border-white/10 rounded-xl p-4" data-testid="gaps-stat-clusters">
            <p className="text-2xl font-display font-bold text-cyan-300">{data.cluster_count}</p>
            <p className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 mt-1">Gap themes</p>
          </div>
          <div className="bg-black/30 border border-white/10 rounded-xl p-4" data-testid="gaps-stat-compartments">
            <p className="text-2xl font-display font-bold text-amber-300">{data.compartments.length}</p>
            <p className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 mt-1">Projects w/ gaps</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : !data || data.clusters.length === 0 ? (
        <div className="text-center py-20" data-testid="gaps-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-400/60 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No documentation gaps detected yet.</p>
          <p className="text-slate-600 text-xs mt-1">As AVA answers questions across the fleet, anything she can't answer well will surface here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.clusters.map((c) => {
            const open = expanded === c.signature;
            return (
              <div key={c.signature} data-testid={`gap-cluster-${c.signature}`} className={`rounded-xl border transition-colors ${c.resolved ? "border-emerald-400/20 bg-emerald-400/[0.03]" : "border-white/10 bg-black/20"}`}>
                <button
                  onClick={() => setExpanded(open ? null : c.signature)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                >
                  {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200 font-medium capitalize truncate">{c.label}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {c.compartments.map((comp) => (
                        <span key={comp} className="text-[9px] font-mono-data text-cyan-300/80 bg-cyan-400/10 border border-cyan-400/20 rounded-full px-2 py-0.5">{comp}</span>
                      ))}
                      <span className="text-[9px] font-mono-data text-slate-500">{c.first_seen} → {c.last_seen}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.unanswered > 0 && (
                      <span className="flex items-center gap-1 text-[10px] font-mono-data text-rose-300" title="Unanswered">
                        <AlertTriangle className="w-3 h-3" /> {c.unanswered}
                      </span>
                    )}
                    <span className="text-lg font-display font-bold text-white" title="Total questions in this theme">{c.count}</span>
                  </div>
                </button>

                {open && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="space-y-1.5">
                      {c.examples.map((ex, i) => (
                        <p key={i} className="text-[12px] text-slate-400 pl-6 border-l border-white/10">
                          "{ex.question}" <span className="text-slate-600">- {ex.compartment}</span>
                        </p>
                      ))}
                    </div>

                    {suggestions[c.signature] && (
                      <div className="bg-black/40 border border-cyan-400/20 rounded-lg p-3 mt-2" data-testid={`gap-suggestion-${c.signature}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[9px] font-mono-data uppercase tracking-wider text-cyan-400 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Suggested documentation fix</span>
                          <button onClick={() => copySuggestion(c.signature, suggestions[c.signature])} className="text-[9px] font-mono-data uppercase text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                            {copied === c.signature ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{copied === c.signature ? "Copied" : "Copy"}
                          </button>
                        </div>
                        <pre className="text-[12px] text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">{suggestions[c.signature]}</pre>
                      </div>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                      <button
                        data-testid={`gap-suggest-${c.signature}`}
                        onClick={() => handleSuggest(c)}
                        disabled={suggesting === c.signature}
                        className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-cyan-300 bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
                      >
                        {suggesting === c.signature ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {suggestions[c.signature] ? "Redraft fix" : "Draft fix"}
                      </button>
                      {c.resolved ? (
                        <button data-testid={`gap-reopen-${c.signature}`} onClick={() => handleResolve(c, false)} className="text-[10px] font-mono-data uppercase tracking-wider text-slate-400 border border-white/10 hover:border-white/20 rounded-full px-3 py-1.5 transition-colors">Reopen</button>
                      ) : (
                        <button data-testid={`gap-resolve-${c.signature}`} onClick={() => handleResolve(c, true)} className="flex items-center gap-1.5 text-[10px] font-mono-data uppercase tracking-wider text-emerald-300 bg-emerald-400/10 hover:bg-emerald-400/20 border border-emerald-400/30 rounded-full px-3 py-1.5 transition-colors">
                          <CheckCircle2 className="w-3 h-3" /> Mark resolved
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
